<script setup lang="ts">
import { ref } from 'vue'
import CopyEntityLink from '@/components/createdUi/CopyEntityLink.vue'
import JournalExportModal from '../modals/JournalExportModal.vue'
import { ERouteNames } from '@/router/ERouteNames'

interface Props {
  journalId: number
  searchQuery: string
  journalName?: string
  canCreate?: boolean
}

interface Emits {
  (e: 'create'): void
  (e: 'update:searchQuery', value: string): void
}

const props = withDefaults(defineProps<Props>(), {
  journalName: '',
  canCreate: false,
})
const emit = defineEmits<Emits>()

const isExportModalOpen = ref(false)

const handleCreate = () => emit('create')

const handleSearchUpdate = (value: string | null) => {
  emit('update:searchQuery', value?.trim() || '')
}

const openExportModal = () => isExportModalOpen.value = true

const shareJournalTo = computed(() => ({
  name: ERouteNames.JOURNAL_DETAIL,
  params: { id: props.journalId },
}))
</script>

<template>
  <div class="d-flex journal-detail-header justify-space-between gap-4 mb-5">
    <div class="d-flex align-center gap-2">
      <h2 v-if="journalName" class="text-h5 font-weight-bold">
        {{ journalName }}
      </h2>
      <CopyEntityLink
        v-if="journalId"
        variant="inline"
        :to="shareJournalTo"
      />
    </div>

    <div class="d-flex align-center gap-2 download-journal">
      <VBtn v-if="canCreate" prepend-icon="ri-add-line" @click="handleCreate">
        Добавить запись
      </VBtn>
      <VBtn prepend-icon="ri-download-2-line" @click="openExportModal" />
    </div>

    <div class="d-flex align-center gap-4 flex-grow-1 justify-end">
      <div class="search-filter w-100">
        <VTextField
          :model-value="searchQuery"
          density="compact"
          placeholder="Поиск..."
          clearable
          prepend-inner-icon="ri-search-line"
          @update:model-value="handleSearchUpdate"
        />
      </div>
    </div>
  </div>

  <JournalExportModal v-model="isExportModalOpen" :journal-id="journalId" />
</template>

<style lang="scss">
.journal-detail-header {
  align-items: center;

  @media (max-width: 680px) {
    flex-direction: column;
    align-items: start;
    > div { width: 100%; }
  }
}

.download-journal .v-btn__prepend { margin-inline: 0; }

.search-filter {
  max-width: 320px;
  inline-size: auto;

  @media (max-width: 990px) { max-width: 100%; }
}
</style>
